import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '@/views/HomeView.vue'
import { getStoredUser, isAuthenticated } from '@/api/client'
import { useI18nLite } from '@/composables/useI18n'

const { t } = useI18nLite()

import type { Component } from 'vue'

/** 统一的懒加载路由条目。
 *
 * 二十来个 {path,name,component,meta} 同构对象是本文件 61.9% 重复率的来源,
 * 也是 Sonar new_duplicated_lines_density 门禁 (1.5%) 的主要计数项 —— 新增
 * 页面时这里曾被反复复制。统一走本工厂, 特例 (预载组件 / 无 meta) 保持字面量。
 */
function lazy(
  path: string,
  name: string,
  titleKey: string,
  component: () => Promise<{ default: Component }>
) {
  // titleKey 为空串 = 该页无标题 (如 paper-deployments), 保持无 meta 的原行为
  return titleKey
    ? { path, name, component, meta: { titleKey } }
    : { path, name, component }
}


const router = createRouter({
  history: createWebHistory(),
  routes: [
    lazy('/login', 'login', 'auth.tab.login', () => import('@/views/LoginView.vue')),
    {
      path: '/',
      name: 'home',
      component: HomeView,
      meta: { titleKey: 'nav.analyze' }
    },
    lazy('/history', 'history', 'nav.history', () => import('@/views/HistoryView.vue')),
    lazy('/market', 'market', 'nav.market', () => import('@/views/MarketReviewView.vue')),
    lazy('/portfolio', 'portfolio', 'nav.portfolio', () => import('@/views/PortfolioView.vue')),
    lazy('/alerts', 'alerts', 'nav.alerts', () => import('@/views/AlertsView.vue')),
    lazy('/batch', 'batch', 'nav.batch', () => import('@/views/BatchView.vue')),
    lazy('/tasks', 'tasks', 'nav.tasks', () => import('@/views/TasksView.vue')),
    lazy('/chat', 'chat', 'nav.chat', () => import('@/views/ChatView.vue')),
    lazy('/watchlist', 'watchlist', 'nav.watchlist', () => import('@/views/WatchlistView.vue')),
    lazy('/screener', 'screener', 'nav.screener', () => import('@/views/ScreenerView.vue')),
    lazy('/factors', 'factors', 'nav.factors', () => import('@/views/FactorsView.vue')),
    lazy('/portfolio-backtest', 'portfolio-backtest', 'nav.portfolioBacktest', () => import('@/views/PortfolioBacktestView.vue')),
    lazy('/factor-backtest', 'factor-backtest', 'nav.factorBacktest', () => import('@/views/FactorBacktestView.vue')),
    lazy('/case-library', 'case-library', 'nav.caseLibrary', () => import('@/views/CaseLibraryView.vue')),
    lazy('/paper-deployments', 'paper-deployments', 'nav.paperDeployments', () => import('@/views/PaperDeploymentsView.vue')),
    lazy('/strategy-lab', 'strategy-lab', 'nav.strategyLab', () => import('@/views/StrategyLabView.vue')),
    lazy('/retrospective', 'retrospective', 'nav.retrospective', () => import('@/views/RetrospectiveView.vue')),
    lazy('/anomaly-scanner', 'anomaly-scanner', 'nav.anomalyScanner', () => import('@/views/AnomalyScannerView.vue')),
    {
      ...lazy('/settings', 'settings', 'nav.settings', () => import('@/views/SettingsView.vue')),
      // ALENS-107 #13: admin UI 结构不对普通用户暴露 (后端 403 只是兜底)
      meta: { titleKey: 'nav.settings', requiresAdmin: true },
    },
    {
      // ALENS-193: 管理员决策评估工作台 (agent-backtest 五端点的前端入口);
      // 非管理员隐藏入口 + 路由守卫, 后端 get_current_admin_user 403 兜底
      ...lazy('/agent-backtest', 'agent-backtest', 'nav.agentBacktest', () => import('@/views/AgentBacktestView.vue')),
      meta: { titleKey: 'nav.agentBacktest', requiresAdmin: true },
    },
    lazy('/funds', 'funds', 'nav.funds', () => import('@/views/FundScreenerView.vue')),
    lazy('/stock-compare', 'stock-compare', 'nav.stockCompare', () => import('@/views/StockCompareView.vue')),
    lazy('/auto-research', 'auto-research', 'nav.autoResearch', () => import('@/views/AutoResearchView.vue')),
    lazy('/research-ops', 'research-ops', 'nav.researchOps', () => import('@/views/ResearchOpsView.vue')),
    lazy('/macro', 'macro', 'nav.macro', () => import('@/views/MacroInsightView.vue')),
    lazy('/sector-rrg', 'sector-rrg', 'nav.sectorRrg', () => import('@/views/SectorRrgView.vue')),
    lazy('/fund-flow', 'fund-flow', 'nav.fundFlow', () => import('@/views/FundFlowView.vue')),
    lazy('/arbitrage', 'arbitrage', 'nav.arbitrage', () => import('@/views/ArbitrageView.vue')),
    lazy('/reports', 'reports', 'nav.reports', () => import('@/views/ReportsView.vue')),
    lazy('/earnings-calendar', 'earnings-calendar', 'nav.earningsCalendar', () => import('@/views/EarningsCalendarView.vue')),
    lazy('/options', 'options', 'nav.options', () => import('@/views/OptionsView.vue')),
    lazy('/cbond', 'cbond', 'nav.cbond', () => import('@/views/CbondWorkbenchView.vue')),
    lazy('/thesis', 'thesis-workbench', 'nav.thesisWorkbench', () => import('@/views/ThesisWorkbenchView.vue')),
    // 兜底 404: 未匹配路径统一导向首页。此前死链 (如曾经的 /research) 会让
    // router-view 渲染空, 主内容区白屏且无任何提示 (ALENS-129)。
    { path: '/:pathMatch(.*)*', redirect: '/' }
  ]
})

router.beforeEach((to, _from, next) => {
  // 标题经 i18n (meta.titleKey), 语言切换后下一次导航即生效
  if (to.meta?.titleKey) {
    document.title = `${t(to.meta.titleKey as string)} - AlphaLens`
  }

  if (to.meta?.requiresAdmin && getStoredUser()?.role !== 'admin') {
    next({ name: 'home' })
    return
  }

  const authed = isAuthenticated()
  if (to.name !== 'login' && !authed) {
    next({ name: 'login', query: { redirect: to.fullPath } })
  } else if (to.name === 'login' && authed) {
    next({ name: 'home' })
  } else {
    next()
  }
})

// ── 动态导入失败兜底 ────────────────────────────────────────────────
// 部署会删掉旧的内容哈希 chunk。若一个标签页在部署**之前**就打开着, 它内存里
// 的懒加载 chunk 引用会指向已删除的文件 (实测旧 CaseLibraryView-<hash>.js 返回 404),
// 组件 import 失败 ⇒ vue-router 渲染不出 router-view ⇒ 页面只剩外壳、正文纯白。
//
// 这里把"永久白屏"降级成"自动刷新一次就好了": 用 sessionStorage 打标保证只做一次,
// 否则新 bundle 加载后再失败会变成无限刷新。
const CHUNK_RELOAD_FLAG = 'alphalens:chunk-reload'

// 错误对象提取为纯文本消息。S6551: 对未知对象直接 String() 会得到
// '[object Object]', 对含循环引用的对象 JSON.stringify 会抛错 —— 两条
// 隐式字符串化路径都要显式兜住。
function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message
  if (typeof err === 'string') return err
  try {
    return JSON.stringify(err) ?? ''
  } catch {
    return ''
  }
}

function isChunkLoadError(err: unknown): boolean {
  const msg = errorMessage(err)
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('ChunkLoadError') ||
    msg.includes('Loading chunk')
  )
}

router.onError((to, _from, err) => {
  if (!isChunkLoadError(err)) {
    console.error('[router] 导航失败', to.fullPath, err)
    return
  }
  if (sessionStorage.getItem(CHUNK_RELOAD_FLAG) === '1') {
    // 已经自动刷新过一次还是失败 ⇒ 不是陈旧引用, 别再刷了
    console.error('[router] chunk 加载失败(已自动刷新过一次)', to.fullPath, err)
    return
  }
  sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1')
  console.warn('[router] chunk 加载失败, 自动刷新一次以取到新 bundle', to.fullPath)
  window.location.reload()
})

// 导航成功一次即视为"当前 bundle 是对的", 解除刷新标记。
router.afterEach(() => {
  if (sessionStorage.getItem(CHUNK_RELOAD_FLAG) === '1') {
    sessionStorage.removeItem(CHUNK_RELOAD_FLAG)
  }
})

export default router
